import { Component, OnDestroy, OnInit, inject, TemplateRef, ViewChild, AfterViewInit, ElementRef } from '@angular/core';
import { MaterialModule } from '../material.module';
import { CommonModule } from '@angular/common';
import { OperationsDashboardService, OperationsDashboard } from '../services/operations-dashboard.service';
import { DataGridComponent } from '../shared/data-grid/data-grid.component';
import { SpinnerComponent } from '../shared/spinner/spinner.component';

// ── Constants (matches legacy) ──────────────────────────────────────────────────────────────────

// const REFRESH_INTERVAL_MS = 5000; // dashboardRefreshTimeoutMilliseconds (increased from 3000 to 5000)
// const ALERTS_COOKIE_KEY = 'cemsys_dashboard_alerts';
// const ALERTS_COOKIE_DAYS = 30; // alertsActiveExpirationDays

// ── Typed view models ──────────────────────────────────────────────────────────

export interface DashboardTest {
  testId: number;
  testInputTypeName: string | null;
  testLastUpdateDateString: string | null;
  statusClass: string;
  alertClass: string;
  testInputStatusName: string | null; // <-- add this
}

export interface DashboardRow {
  projectId: number;
  projectName: string | null;
  rigName: string | null;
  jobType: string | null;
  projectLastUpdateDateString: string | null;
  projectAlertClass: string;
  slurryId: number;
  slurryName: string | null;
  slurryLastUpdateDateString: string | null;
  slurryAlertClass: string;
  tests: DashboardTest[];
}

const STATUS_CLASS_MAP: Record<string, string> = {
  NotStarted: 'not-started',
  InProgress: 'in-progress',
  Completed: 'completed',
  Error: 'error',
};

@Component({
  selector: 'app-operations-dashboard',
  standalone: true,
  imports: [MaterialModule, CommonModule, DataGridComponent, SpinnerComponent],
  templateUrl: './operations-dashboard.component.html',
  styleUrls: ['./operations-dashboard.component.css'],
})
export class OperationsDashboardComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('projectUfidCell', { static: true }) projectUfidCell!: TemplateRef<any>;
  @ViewChild('slurryNameCell', { static: true }) slurryNameCell!: TemplateRef<any>;
  @ViewChild('testsCell', { static: true }) testsCell!: TemplateRef<any>;
  @ViewChild('audioControl', { static: false }) audioControl!: ElementRef<HTMLAudioElement>;

  // Legacy state variables (matches dashboard.js)
  isLoading: boolean = true;
  isFullScreen: boolean = false;  // isViewToggled in legacy
  alertsActive: boolean = false;  // alertsStatus == 'Active'
  alertsFound: boolean = false;
  isInitialized: boolean = false;
  dataSource: DashboardRow[] = [];  // table data
  refreshed: string = '';
  computerName: string = '';  // matches $('#computerName').val()
  gridColumns: any[] = [];

  private readonly dashboardService = inject(OperationsDashboardService);
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;  // this.timer
  private alertsClear = false;  // self.alertsClear
  private alertsActivate = false;  // self.alertsActivate
  private alertsWaitCounter = 0;  // self.alertsWaitCounter
  private dataRows: OperationsDashboard[] = [];  // this.dataRows

  // Matches legacy: document.getElementById('toggleAlerts').text logic
  get alertsButtonLabel(): string {
    if (!this.isInitialized) return 'Toggle Alerts';
    return this.alertsActive ? 'Disable Alerts' : 'Enable Alerts';
  }

  // Matches legacy: document.getElementById('toggleClear').text logic
  // Button shows when alerts are active (enabled), text changes based on whether alerts are found
  get clearButtonText(): string {
    if (!this.alertsActive) return '';
    return this.alertsFound ? 'Clear Current Alerts' : 'Clear Current Alerts';
  }

  // Clear button visibility - shows when alerts are enabled
  get showClearButton(): boolean {
    return this.alertsActive;
  }

  // Matches legacy: $(window).ready initialization
  ngOnInit(): void {
  // dashboard.showDataLoader();
  this.isLoading = true;
  // dashboard.alertsStatus = $.cookie('cemsys_dashboard_alerts');
  // this.alertsActive = this.getCookie(ALERTS_COOKIE_KEY) === 'Active';
  // Always use blank computerName
  this.computerName = '';
  this.startPolling();
  }

  // Removed initializeComputerName: always use blank computerName

  ngAfterViewInit(): void {
    this.gridColumns = [
      {
        key: 'projectUfid',
        label: 'PROJECT UFID',
        cellAlign: 'left',
        width: '160px',
        customTemplate: this.projectUfidCell
      },
      {
        key: 'slurryName',
        label: 'SLURRY NAME',
        accessor: (row: any) => row.slurryName,
        cellAlign: 'left',
        width: '130px',
        customTemplate: this.slurryNameCell
      },
      {
        key: 'tests',
        label: 'TESTS',
        accessor: (row: any) => row.tests?.map((t: any) => t.testInputTypeName).join(', '),
        cellAlign: 'left',
        width: '300px',
        customTemplate: this.testsCell
      }
    ];
  }

  ngOnDestroy(): void {
    this.stopTimer();
  }

  private startPolling(): void {
    this.fetchAndProcess();
  }

  private scheduleRefresh(): void {
    this.stopTimer();
    // this.refreshTimer = setTimeout(() => this.fetchAndProcess(), REFRESH_INTERVAL_MS);
  }

  private stopTimer(): void {
    if (this.refreshTimer !== null) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Matches legacy: dashboard.handleUpdate
  private fetchAndProcess(): void {
    // self.alertsStatus = $.cookie('cemsys_dashboard_alerts');
    // this.alertsActive = this.getCookie(ALERTS_COOKIE_KEY) === 'Active';
    
    // let apiURL = 'api/dashboard/web'; if (self.alertsStatus == 'Active') { apiURL = 'api/dashboard/web/' + dashboard.computerName; }
    const request$ = this.alertsActive && this.computerName
      ? this.dashboardService.getDashboardAlertsForComputer(this.computerName)
      : this.dashboardService.getOperationsDashboard();

    request$.subscribe({
      next: (data) => { 
        // Matches legacy: setTimeout(function () { dashboard.updateTests(data); dashboard.hideDataLoader(); }, 200);
        setTimeout(() => {
          this.updateTests(data);
        }, 200);
      },
      error: (err) => {
        console.error('Failed to load dashboard data', err);
        this.isLoading = false;
      },
      complete: () => {
        // Matches legacy: .always(function () { self.timer = setTimeout(self.handleUpdate, dashboardRefreshTimeoutMilliseconds); })
        this.scheduleRefresh();
      }
    });
  }

  // Matches legacy: this.updateTests = function (dataRows)
  private updateTests(dataRows: OperationsDashboard[]): void {
    try {
      this.processUpdateTests(dataRows);
    } catch (e) {
      console.log("Caught an exception: " + e);
      alert("Caught an exception: " + e);
    }
  }

  // Matches legacy: let processUpdateTests = function (dataRows)
  private processUpdateTests(dataRows: OperationsDashboard[]): void {
    // if (dashboard.alertsClear)
    if (this.alertsClear) {
      this.clearCurrent();
      this.alertsClear = false;
      this.alertsWaitCounter = 1;
    }
    // else if (dashboard.alertsActivate)
    else if (this.alertsActivate) {
      this.alertsActivate = false;
      this.alertsWaitCounter = 1;
    }
    // else if (dashboard.alertsWaitCounter != 0)
    else if (this.alertsWaitCounter !== 0) {
      this.alertsWaitCounter -= 1;
      this.hideDataLoader();
    }
    // else { dashboard.dataRows = dataRows; dashboard._initTable(); }
    else {
      this.dataRows = dataRows;
      this._initTable();
    }
  }

  // Matches legacy: this.clearCurrent
  private clearCurrent(): void {
    const computerName = this.computerName;
    
    this.dashboardService.clearDashboardAlertsForComputer(computerName)
      .subscribe({
        next: (data) => {
          console.log('[Dashboard] Alerts cleared:', data);
        },
        error: (err) => {
          alert(err);
        }
      });
  }

  // Matches legacy: this.hideDataLoader
  private hideDataLoader(): void {
    this.isLoading = false;
  }

  // Matches legacy: this._initTable
  private _initTable(): void {
    // self.alertsStatus = $.cookie('cemsys_dashboard_alerts');
    //this.alertsActive = this.getCookie(ALERTS_COOKIE_KEY) === 'Active';
    
    // Update button labels
    // if (self.alertsStatus == 'Active') { document.getElementById('toggleAlerts').text = "Disable Alerts"; }
    // else { document.getElementById('toggleAlerts').text = "Enable Alerts"; }
    // (handled by getter alertsButtonLabel)
    
    // self.alertsFound = false;
    this.alertsFound = false;
    
    // processDataRows(this.dataRows);
    this.dataSource = this.processDataRows(this.dataRows);
    
    // if (self.alertsFound) { document.getElementById('audioControl').play(); document.getElementById('toggleClear').text = "Clear Current Alerts"; }
    // else { document.getElementById('audioControl').pause(); document.getElementById('toggleClear').text = ""; }
    console.log('[Dashboard] _initTable: alertsActive=', this.alertsActive, 'alertsFound=', this.alertsFound, 'clearButtonText=', this.clearButtonText);
    if (this.alertsFound) {
      this.playAudio();
    } else {
      this.pauseAudio();
    }
    
    // self.isInitialized = true;
    this.isInitialized = true;
    
    // Update datetime (matches legacy formatting)
    const currentdate = new Date();
    this.refreshed = 
      (currentdate.getMonth() + 1) + "/" +
      currentdate.getDate() + "/" +
      currentdate.getFullYear() + " " +
      currentdate.getHours() + ":" +
      currentdate.getMinutes().toString() + ":" +
      currentdate.getSeconds().toString();
    
    // Hide loader
    this.isLoading = false;
  }

  // Matches legacy: let processDataRows = function (dataRows)
  private processDataRows(dataRows: OperationsDashboard[]): DashboardRow[] {
    const dataRowsCount = dataRows.length;
    
    // dataRows.sort((a, b) => (b.projectId - a.projectId) || (a.slurryId - b.slurryId) || (a.testId - b.testId));
    dataRows.sort((a, b) =>
      (b.projectId - a.projectId) || (a.slurryId - b.slurryId) || (a.testId - b.testId)
    );
    
    const map = new Map<string, DashboardRow>();
    let currProjectID = 0;
    let currSlurryID = 0;
    
    // $.each(dataRows, function (i, dataRow) { ... })
    dataRows.forEach((dataRow, i) => {
      // if (dataRow.projectId != dashboard.currProjectID || dataRow.slurryId != dashboard.currSlurryID)
      if (dataRow.projectId !== currProjectID || dataRow.slurryId !== currSlurryID) {
        currProjectID = dataRow.projectId;
        currSlurryID = dataRow.slurryId;
        
        const key = `${dataRow.projectId}-${dataRow.slurryId}`;
        
        // createRow(dataRow) - creates project and slurry cells
        const projectAlertClass = this.getAlertClass(dataRow.projectAlertClass);
        const slurryAlertClass = this.getAlertClass(dataRow.slurryAlertClass);
        
        // Check for alerts
        if (projectAlertClass) {
          this.alertsFound = true;
        }
        if (slurryAlertClass) {
          this.alertsFound = true;
        }
        
        map.set(key, {
          projectId: dataRow.projectId,
          projectName: dataRow.projectName ?? null,
          rigName: dataRow.rigName ?? null,
          jobType: dataRow.jobType ?? null,
          projectLastUpdateDateString: dataRow.projectLastUpdateDateString ?? null,
          projectAlertClass,
          slurryId: dataRow.slurryId,
          slurryName: dataRow.slurryName ?? null,
          slurryLastUpdateDateString: dataRow.slurryLastUpdateDateString ?? null,
          slurryAlertClass,
          tests: [],
        });
      }
      
      // if (dataRow.testId != 0) { let testSpan = createTestSpan(dataRow); ... }
      if (dataRow.testId !== 0) {
        const key = `${dataRow.projectId}-${dataRow.slurryId}`;
        const test = this.createTestSpan(dataRow);
        if (test) {
          map.get(key)!.tests.push(test);
        }
      }
    });
    
    return Array.from(map.values());
  }

  // Matches legacy: let createTestSpan = function (dataRow)
  private createTestSpan(dataRow: OperationsDashboard): DashboardTest | null {
    // if (dataRow.testInputID != 0)
    if (dataRow.testInputID !== 0) {
      const statusClass = dataRow.testInputStatusClass ?? '';
      const alertClass = dataRow.testAlertClass ?? '';
      
      // if (dashboard.alertsStatus == 'Active' && alertClass != "" && statusClass != "testInProgress")
      const isAlert = this.alertsActive && 
                     alertClass !== '' && 
                     statusClass !== 'testInProgress';
      
      if (isAlert) {
        // dashboard.alertsFound = true;
        this.alertsFound = true;
        console.log('[Dashboard] Test alert found:', dataRow.testInputTypeName, alertClass);
      }
      
      return {
        testId: dataRow.testId,
        testInputTypeName: dataRow.testInputTypeName ?? null,
        testLastUpdateDateString: dataRow.testLastUpdateDateString ?? null,
        statusClass: statusClass,
        alertClass: isAlert ? alertClass : '',
        testInputStatusName: dataRow.testInputStatusName ?? null,
      };
    }
    
    return null;
  }

  // Helper: Get alert class only when alerts are active
  private getAlertClass(alertClass: string | undefined | null): string {
    // if (dashboard.alertsStatus == 'Active' && alertClass !== null && alertClass != "")
    return (this.alertsActive && alertClass !== null && alertClass !== undefined && alertClass !== '') 
      ? alertClass 
      : '';
  }

  // Matches legacy: processToggleView
  toggleFullView(): void {
    this.isFullScreen = !this.isFullScreen;
    document.body.style.overflow = this.isFullScreen ? 'hidden' : '';
    // Legacy would call _initTable here, but we don't need to rebuild the entire table
  }

  // Matches legacy: processToggleAlerts
  toggleAlerts(): void {
    // Re-check current status
    //this.alertsActive = this.getCookie(ALERTS_COOKIE_KEY) === 'Active';
    
    // if (dashboard.alertsStatus == 'Active')
    if (this.alertsActive) {
      // Disabling alerts
      console.log('[Dashboard] Disabling alerts...');
      
      // document.getElementById('audioControl').pause();
      this.pauseAudio();
      
      // document.getElementById('toggleClear').text = "";
      this.alertsFound = false;
      
      // $.removeCookie('cemsys_dashboard_alerts');
      // this.deleteCookie(ALERTS_COOKIE_KEY);
      
      this.alertsActive = false;
      this.alertsActivate = false;
    } else {
      // Enabling alerts
      console.log('[Dashboard] Enabling alerts...');
      
      // $.cookie('cemsys_dashboard_alerts', 'Active', { expires: alertsActiveExpirationDays });
      // this.setCookie(ALERTS_COOKIE_KEY, 'Active', ALERTS_COOKIE_DAYS);
      
      // self.alertsActivate = true;
      this.alertsActivate = true;
      this.alertsActive = true;
    }
  }

  // Matches legacy: toggleClear.click
  clearCurrentAlerts(): void {
    console.log('[Dashboard] Clearing alerts for computer:', this.computerName);
    
    // document.getElementById('audioControl').pause();
    this.pauseAudio();
    
    // self.alertsClear = true;
    this.alertsClear = true;
  }

  getGridConfig() {
    return {
      enableSorting: true,
      stickyHeader: true
    };
  }

  // ── Audio Control (matches legacy JS) ────────────────────────────────────────

  private playAudio(): void {
    if (this.audioControl?.nativeElement) {
      this.audioControl.nativeElement.play().catch(err => console.log('Audio play failed:', err));
    }
  }

  private pauseAudio(): void {
    if (this.audioControl?.nativeElement) {
      this.audioControl.nativeElement.pause();
    }
  }

  // ── Cookie Helpers ────────────────────────────────────────────────────────────

  private getCookie(name: string): string {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  private setCookie(name: string, value: string, days: number): void {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}
